/**
 * Infrastructure Monitoring Page
 *
 * Admin page for monitoring and managing RabbitMQ and Redis infrastructure.
 * Features: Four-tab interface (RabbitMQ Overview | Queue Management | Redis Overview | Cache Management)
 */

import { useState, useEffect } from 'react';
import {
  IconServer,
  IconRefresh,
  IconAlertCircle,
  IconMessageCircle,
  IconDatabase,
  IconSettings,
  IconList,
  IconActivity,
  IconKey,
} from '@tabler/icons-react';
import { Container, Stack, Group, Text, Title, Button, Alert, Tabs, Loader } from '../../../components/ui';
import { FadeIn, SlideIn } from '../../../components/animations/transitions';
import { useRabbitMqMonitoring } from './hooks/useRabbitMqMonitoring';
import { useRedisMonitoring } from './hooks/useRedisMonitoring';
import { RabbitMqOverviewTab, QueueManagementTab, MessageFlowTab, RedisOverviewTab, CacheExplorerTab, CacheManagementTab } from './components';

export default function InfrastructureMonitoring() {
  const [activeTab, setActiveTab] = useState<string>('rabbitmq-overview');

  const {
    connectionStats,
    queueStats,
    loading: rabbitLoading,
    error: rabbitError,
    setError: setRabbitError,
    fetchAll: fetchRabbitMq,
    deleteQueue,
    purgeQueue,
  } = useRabbitMqMonitoring();

  const {
    health,
    statistics,
    serviceGroups,
    domainStats,
    loading: redisLoading,
    error: redisError,
    setError: setRedisError,
    fetchAll: fetchRedis,
    deleteByPattern,
  } = useRedisMonitoring();

  const loading = rabbitLoading || redisLoading;
  const error = rabbitError || redisError;

  // Initial fetch on mount
  useEffect(() => {
    fetchRabbitMq();
    fetchRedis();
  }, [fetchRabbitMq, fetchRedis]);

  const handleRefresh = () => {
    if (activeTab.startsWith('rabbitmq')) {
      fetchRabbitMq();
    } else {
      fetchRedis();
    }
  };

  const handleClearError = () => {
    setRabbitError(null);
    setRedisError(null);
  };

  return (
    <Container size="xl" py="xl">
      <FadeIn delay={100}>
        <Stack gap="xl">
          {/* HEADER */}
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="sm">
                <IconServer size={32} />
                <Title order={2}>Infrastructure Monitoring</Title>
              </Group>
              <Text size="sm" c="dimmed">
                Monitor and manage RabbitMQ queues and Redis cache
              </Text>
            </Stack>
            <Button
              onClick={handleRefresh}
              loading={loading}
              leftSection={<IconRefresh size={16} />}
              variant="subtle"
            >
              Refresh
            </Button>
          </Group>

          {/* ERROR ALERT */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={handleClearError}
            >
              {error}
            </Alert>
          )}

          {/* TABS */}
          <SlideIn direction="up" delay={150}>
            <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'rabbitmq-overview')}>
              <Tabs.List>
                <Tabs.Tab value="rabbitmq-overview" leftSection={<IconMessageCircle size={16} />}>
                  RabbitMQ Overview
                </Tabs.Tab>
                <Tabs.Tab value="message-flow" leftSection={<IconActivity size={16} />}>
                  Message Flow
                </Tabs.Tab>
                <Tabs.Tab value="queue-management" leftSection={<IconList size={16} />}>
                  Queue Management
                </Tabs.Tab>
                <Tabs.Tab value="redis-overview" leftSection={<IconDatabase size={16} />}>
                  Redis Overview
                </Tabs.Tab>
                <Tabs.Tab value="cache-explorer" leftSection={<IconKey size={16} />}>
                  Cache Explorer
                </Tabs.Tab>
                <Tabs.Tab value="cache-management" leftSection={<IconSettings size={16} />}>
                  Cache Management
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="rabbitmq-overview" pt="md">
                {rabbitLoading && !connectionStats ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <RabbitMqOverviewTab
                    connectionStats={connectionStats}
                    queueStats={queueStats}
                    onRefresh={fetchRabbitMq}
                    loading={rabbitLoading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="message-flow" pt="md">
                <MessageFlowTab />
              </Tabs.Panel>

              <Tabs.Panel value="queue-management" pt="md">
                {rabbitLoading && !queueStats ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <QueueManagementTab
                    queues={queueStats?.queues || []}
                    onRefresh={fetchRabbitMq}
                    onQueueDelete={deleteQueue}
                    onQueuePurge={purgeQueue}
                    loading={rabbitLoading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="redis-overview" pt="md">
                {redisLoading && !health ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <RedisOverviewTab
                    health={health}
                    statistics={statistics}
                    onRefresh={fetchRedis}
                    loading={redisLoading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="cache-explorer" pt="md">
                <CacheExplorerTab />
              </Tabs.Panel>

              <Tabs.Panel value="cache-management" pt="md">
                {redisLoading && Object.keys(serviceGroups).length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <CacheManagementTab
                    serviceGroups={serviceGroups}
                    domainStats={domainStats}
                    onRefresh={fetchRedis}
                    onDeleteByPattern={deleteByPattern}
                    onDeleteKey={async () => {
                      // Implement if needed in future
                    }}
                    loading={redisLoading}
                  />
                )}
              </Tabs.Panel>
            </Tabs>
          </SlideIn>
        </Stack>
      </FadeIn>
    </Container>
  );
}
